/**
 * @fileoverview The one confirmation dialog every power bank action goes
 * through. It owns the in-flight state so no caller can forget it: while
 * `onConfirm` runs both buttons are disabled, the confirm button shows a
 * spinner, the backdrop is locked, and a thrown error is shown inside the
 * dialog instead of closing it silently.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'

import { Modal } from 'Components/Modal'
import { ModalBody } from 'Components/Modal/ModalBody'
import { ModalFooter } from 'Components/Modal/ModalFooter'
import { ModalHeader } from 'Components/Modal/ModalHeader'
import { ModalTitle } from 'Components/Modal/ModalTitle'
import SubmitButton from 'Components/Form/SubmitButton'

const Body = styled.div`
  font-family: 'Proxima Nova', sans-serif;
  font-size: 16px;
  line-height: 1.5;
  color: #011533;

  p {
    margin: 0 0 12px 0;
  }
`

export const DialogError = styled.div`
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 4px;
  background: #fdecef;
  color: #c8102e;
  font-size: 14px;
`

const getErrorMessage = error =>
  (error && error.message) || 'Something went wrong, please try again.'

const PowerBankDialog = ({
  show,
  title,
  children,
  confirmLabel,
  cancelLabel,
  confirmDisabled,
  hideCancel,
  secondaryLabel,
  onSecondary,
  onConfirm,
  onCancel,
}) => {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  // A successful action usually closes (unmounts) the dialog from inside
  // the handler, so the state writes after it must be guarded.
  const mountedRef = useRef(true)

  useEffect(
    () => () => {
      mountedRef.current = false
    },
    [],
  )

  useEffect(() => {
    if (!show) {
      setBusy(false)
      setError(null)
    }
  }, [show])

  const run = useCallback(
    async action => {
      if (busy || typeof action !== 'function') return
      setBusy(true)
      setError(null)
      try {
        await action()
      } catch (actionError) {
        if (!mountedRef.current) return
        setError(getErrorMessage(actionError))
        setBusy(false)
        return
      }
      if (mountedRef.current) setBusy(false)
    },
    [busy],
  )

  const handleConfirm = useCallback(() => run(onConfirm), [run, onConfirm])
  const handleSecondary = useCallback(() => run(onSecondary), [
    run,
    onSecondary,
  ])

  const handleCancel = useCallback(() => {
    if (busy) return
    onCancel()
  }, [busy, onCancel])

  return (
    <Modal
      show={show}
      onHide={handleCancel}
      backdrop={busy ? 'static' : true}
      keyboard={!busy}
    >
      <ModalHeader closeButton={!busy}>
        <ModalTitle>{title}</ModalTitle>
      </ModalHeader>
      <ModalBody>
        <Body>
          {children}
          {error && <DialogError role="alert">{error}</DialogError>}
        </Body>
      </ModalBody>
      <ModalFooter>
        {!hideCancel && (
          <SubmitButton
            value={cancelLabel}
            variant="outlined"
            onClick={handleCancel}
            disabled={busy}
          />
        )}
        {onSecondary && (
          <SubmitButton
            value={secondaryLabel}
            variant="outlined"
            onClick={handleSecondary}
            disabled={busy}
          />
        )}
        <SubmitButton
          value={confirmLabel}
          onClick={handleConfirm}
          disabled={busy || confirmDisabled}
          formSubmitting={busy}
        />
      </ModalFooter>
    </Modal>
  )
}

PowerBankDialog.propTypes = {
  show: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  children: PropTypes.node,
  confirmLabel: PropTypes.string,
  cancelLabel: PropTypes.string,
  confirmDisabled: PropTypes.bool,
  hideCancel: PropTypes.bool,
  /** Optional second async action ("No" next to "Yes"), same handling. */
  secondaryLabel: PropTypes.string,
  onSecondary: PropTypes.func,
  /** Async. Throw to keep the dialog open and show the error. */
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
}

PowerBankDialog.defaultProps = {
  confirmLabel: 'Confirm',
  cancelLabel: 'Cancel',
  confirmDisabled: false,
  hideCancel: false,
  secondaryLabel: 'No',
  onSecondary: null,
}

export default PowerBankDialog
